import {OcCinemaV1Events} from "@otto-ec/otto-components/cinema";
import GenericTrackingService from "./generic_tracking_service";
import {PictureTextType} from "./types";
import PictureTextElementTile from "./picture_text_element_tile";
import {Action, DataContainer, submitEvent} from "@otto-ec/tracking-bct";

export class PictureTextSlider {
    private readonly trackingService: GenericTrackingService;
    readonly slides: Array<PictureTextElementTile>
    private readonly featureIndex: number;
    private readonly filledSlots: number;
    private readonly featureOrder: number;
    readonly featureId: string;
    readonly parentFeatureId: string;
    private readonly masFeatureTrackingLabels: { [key: string]: string[] } = {};
    private slideEventTrackingBuffer: PictureTextElementTile[] = []
    private trackedTilePositions: number[] = []

    constructor() {
        const pictureTextSliderDom: HTMLElement | null = document.querySelector('.promo_staticcontent--picture-text-slider');
        if (!pictureTextSliderDom) {
            throw new Error("PictureTextSlider DOM element not found");
        }
        this.featureIndex = this.getFeatureIndex(pictureTextSliderDom);
        this.featureOrder = this.getFeatureOrder(pictureTextSliderDom);
        this.featureId = this.getDataFeatureId(pictureTextSliderDom);
        this.parentFeatureId = this.getParentFeatureTrackingId(this.featureId);
        this.masFeatureTrackingLabels = this.getMasFeatureTrackingLabels(pictureTextSliderDom);
        this.trackingService = new GenericTrackingService()

        const slideContainers = Array.from(pictureTextSliderDom.querySelectorAll<HTMLElement>('[data-js="picture-text-slider-tile"]'));
        this.slides = slideContainers.map((slideDom: HTMLElement, index: number) => new PictureTextElementTile(slideDom, index + 1, this.getTileFeatureTrackingId(this.featureId, index + 1)))
        this.filledSlots = this.slides.length;
        slideContainers.forEach((bildTextTileDom: HTMLElement, index: number) => {
            bildTextTileDom.addEventListener("click", this.trackClick(this.slides[index], "Slider"), false);
        });

        this.trackLoaded(pictureTextSliderDom);
        this.registerScrollTrackingObserver();

        const cinemaElement = pictureTextSliderDom.querySelector("oc-cinema-v1");
        cinemaElement!.addEventListener("oc-item-visibility-changed", (event: OcCinemaV1Events["oc-item-visibility-changed"]) => {
            if (event.detail.scrollDirection === "right") {
                this.trackNextSlidePictureTextSlider(event.detail.visibleItems.indices, event.detail.isScrolling);
            }
        });
    }

    sendPictureTextSliderSlideNextTrackingEvent(featureOrder: number, featureIndex: number, masFeatureTrackingLabels: DataContainer, visibleSlides: PictureTextElementTile[], isStageScrolling: boolean, parentTrackingId: string) {
        const slidesForTracking = visibleSlides.filter(slide => !this.trackedTilePositions.includes(slide.position) && !this.slideEventTrackingBuffer.includes(slide));
        this.slideEventTrackingBuffer.push(...slidesForTracking)

        if (!isStageScrolling) {
            const trackingAction: Action = {
                name: "next",
                features: [
                    this.trackingService.createWidgetFeature(featureOrder, featureIndex, parentTrackingId, this.slides.length, "visible", masFeatureTrackingLabels, "Slider"),
                    ...this.slideEventTrackingBuffer.map(((widgetTile: PictureTextElementTile) => {
                        return this.trackingService.createWidgetTileFeature(widgetTile, parentTrackingId, "visible")
                    }))
                ]
            }

            if (this.slideEventTrackingBuffer.length > 0) {
                this.trackedTilePositions.push(...this.slideEventTrackingBuffer.map((slide) => slide.position));
                submitEvent({}, trackingAction)
            }

            this.slideEventTrackingBuffer = []
        }
    }

    registerScrollTrackingObserver(): void {
        /*                                     */
        const observer = new IntersectionObserver((entries) => {
            const entriesToTrack = entries
                .filter((entry) => entry.isIntersecting);

            if (entriesToTrack.length > 0 && this.trackedTilePositions.length <= 0) {
                const positionsToTrack = entriesToTrack.map((entry) => {
                    return parseInt(entry.target.getAttribute("data-tile-position")!);
                })

                this.trackedTilePositions.push(...positionsToTrack)

                const tileFeatureIdsToTrack = positionsToTrack.map((position) => this.getTileFeatureTrackingId(this.featureId, position))
                this.trackingService.sendScrollTrackingEvent(this.parentFeatureId, tileFeatureIdsToTrack);

                entriesToTrack.forEach((entry) => {
                    observer.unobserve(entry.target);
                });
            }
        });

        this.slides.forEach((slide) => {
            observer.observe(slide.tileDom);
        })
    }

    private trackLoaded(container: Element) {
        if (!container.getAttribute("data-tracked")) {
            container.setAttribute("data-tracked", "true");
            this.trackingService.sendLoadedTracking(
                this.featureOrder,
                this.featureIndex,
                this.parentFeatureId,
                this.slides,
                this.masFeatureTrackingLabels,
                "Slider");
        }
    }

    trackNextSlidePictureTextSlider(currentSlidePositions: number[], isScrolling: boolean): void {
        const visibleSlides: PictureTextElementTile[] = []
        currentSlidePositions.forEach(position => {
            if (this.slides[position]) {
                visibleSlides.push(this.slides[position]);
            }
        });
        this.sendPictureTextSliderSlideNextTrackingEvent(this.featureOrder, this.featureIndex, this.masFeatureTrackingLabels, visibleSlides, isScrolling, this.parentFeatureId);
    }

    trackClick(trackingWidgetTile: PictureTextElementTile, tileType: PictureTextType): () => void {
        const self = this;
        return function () {
            self.trackingService.sendClickTrackingEvent(self.featureOrder, self.featureIndex, self.parentFeatureId, trackingWidgetTile, self.filledSlots, self.masFeatureTrackingLabels, tileType);
        }
    }

    private getFeatureOrder(container: Element): number {
        const dataFeatureOrder = container.closest('[data-feature-order]')?.getAttribute('data-feature-order');
        return dataFeatureOrder ? parseInt(dataFeatureOrder, 0) : -1;
    }

    private getFeatureIndex(container: Element): number {
        return parseInt(container.getAttribute('data-feature-index')!, 0);
    }

    private getMasFeatureTrackingLabels(container: Element): DataContainer {
        const dataFeatureTrackingLabels = container.parentElement?.closest('[data-feature-tracking-labels]')?.getAttribute("data-feature-tracking-labels");
        return dataFeatureTrackingLabels ? JSON.parse(dataFeatureTrackingLabels) : {};
    }

    private getDataFeatureId(container: Element): string {
        return container.getAttribute('data-feature-id')!;
    }

    private getParentFeatureTrackingId(featureId: string) {
        return `ft3_pts_${featureId}`;
    }

    private getTileFeatureTrackingId(featureId: string, tilePosition: number) {
        return `ft3_pts_${featureId}_tile${tilePosition}`;
    }
}
